import React, { useEffect, useState } from 'react';
import Loader from '../../components/loader/Loader';
import { isEmpty } from 'ramda';
import debounce from 'debounce';
import { Link } from 'react-router';
import { useLocation } from 'react-router';

import GoBackButton from '../../components/goBackButton/GoBackButton';
import Pagination from '../../components/pagination/Pagination';
import PinPairing from '../../components/pinPairing/PinPairing';
import ErrorMessage from '../../components/errorMessage/ErrorMessage';
import InputField from '../../components/inputField/InputField';
import IconButton from '../../components/iconButton/IconButton';
import Modal from '../../components/modal/Modal';
import Server from '../../js/config/_server';

import { userStore } from '../../store';
import { type UserDealerships } from '../../store/slices/userStore';
import { type Dealership } from '../../api/types/index';
import helpers, { sanitizeCollectionResponse } from '../../js/helpers';
import {
    ROUTES,
} from '../../js/_constants';
import { useTranslation } from 'react-i18next';
import Config from '../../js/config/_global';
import { isNotEmpty } from '../../js/fpUtils';
import api from '../../api';
import './DealershipsView.scss';

const DealershipsView = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const { details, dealerships} = userStore();

    const currentSearch = helpers.getQueryParameter();

    const [error, setError] = useState('');

    const [dealerList, setDealerList] = useState<Dealership[]>([]);
    
    const [currentUserDealerships, setCurrentUserDealerships] = useState<
        UserDealerships | undefined
    >(undefined);

    const [isLoading, setIsLoading] = useState(true);
    const [isMounted, setIsMounted] = useState(true);

    const [isPinPairModalOpen, setIsPinPairModalOpen] = useState(false);
    const [isPortalPairModalOpen, setIsPortalPairModalOpen] = useState(false);

    const [query, setQuery] = useState(currentSearch.query || '');
    const [dealerTitle, setDealerTitle] = useState(currentSearch.query || '');
    const [kvps, setKvps] = useState(currentSearch.kvps || '');

    const controller = new AbortController();
    const signal = controller.signal;

    /**
     * Devices view actually lists devices grouped by users dealerships
     * In order to render devices with pagination we actually query for users dealerships from where we get the pagination.
     * After receiving dealerships for a given page, we then call devices endpoint in order to get device details for rendering required information
     */
    const fetchDealerships = async (
        page,
        dealerTitle='',
        kvps=''
        ) => {
        setDealerTitle(dealerTitle);
        setKvps(kvps);
        setIsLoading(true);
        try {
            const dealershipResponse = await api.user.getUserDealerships({
                params: {
                    brand: details.userGrantedBrands,
                    ...(kvps && {
                        kvps: kvps
                    }),
                    ...(dealerTitle && {
                        title: dealerTitle,
                    }),
                    hasDevices: true,
                    page: page,
                },
                signal
            });

            const nextDealerships =
                sanitizeCollectionResponse<Dealership>(dealershipResponse);
            setDealerList(nextDealerships.list);

            if (isMounted) {
                setCurrentUserDealerships(nextDealerships);
            }
        } catch (error) {
            console.warn(error);
            setError('Failed loading devices. Try again!');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenNewDevice = () => {
        details.isPinPairDisabled ? setIsPortalPairModalOpen(true) : setIsPinPairModalOpen(true);
    };

    const handlePinPairModalClose = event => {
        if (event && event.message === 'success') {
            fetchDealerships(1);
        }
        setIsPinPairModalOpen(false);
    };

    const handlePaginationClick = async (pageNumber: number) => {
            fetchDealerships(
                pageNumber,
                currentSearch.dealerTitle ? currentSearch.dealerTitle : '',
                currentSearch.kvps ? currentSearch.kvps : ''
            );
    };

    const renderPagination = () => {
        if (currentUserDealerships) {
            const { totalPages, currentPage } = currentUserDealerships;
            if (totalPages > 1) {
                return (
                    <Pagination
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onClick={handlePaginationClick}
                    />
                );
            }
        }
    };

    useEffect(() => {
        const currentSearch = helpers.getQueryParameter();

        setQuery(currentSearch.query || '');
  
        fetchDealerships(
            currentSearch.page,
            currentSearch.query || '',
            currentSearch.query || '',
        );
    }, [location]);

    useEffect(() => {
        return () => {
            setIsMounted(false);
        };
    }, []);

    const renderContent = () => {
        if (isNotEmpty(dealerList)) {
            return (
        <div data-testid="dealership-card-list" className="dealership-cards">
            <div className="dealership-card-list">
                {dealerList.map((dealer, i) => (
                    <Link
                        className="dealership-card"
                        key={dealer.kvps}
                        to={`${ROUTES.DEALERSHIPS}/${dealer.kvps}`}
                        data-testid={`dealership-card-${i + 1}`}
                    >
                        <div className='dealership-info'>
                            <p className="dealership-title" data-testid={`dealership-${i + 1}-title`}>{dealer.title}</p>
                            <p className="dealership-kvps" data-testid={`dealership-${i + 1}-kvps`}>{dealer.kvps}</p>
                            <p className="dealership-devices" data-testid={`dealership-${i + 1}-devices-count`}>{dealer.devices.length}{` Device(s)`}</p>
                        </div>

                        <div className='dealership-devices-hint'>
                            {dealer.devices.filter((device,i) => i < 3 ).map((device,i)=> <p key={i} className="dealership-device">{device.split('/')[4]}</p>)}
                            {dealer.devices.length > 3 && <p className='hint-number'>and {dealer.devices.length - 3}{` device(s) more`}</p>}
                        </div>

                        <span className='arrow-front'></span>
                    </Link>
                ))}
            </div>
        </div>
            );
        }

        if (dealerTitle && isEmpty(dealerList)) {
            return (
                <span className="b-dealerships-view__empty-state-message">
                    {t('dealerships_view_no_search_results')}
                </span>
            );
        }

        if (isEmpty(dealerships.list)) {
            return (
                <span className="b-dealerships-view__empty-state-message">
                    {t('dealerships_view_no_dealerships_message')}
                </span>
            );
        }

        if (isNotEmpty(dealerships.list) && isEmpty(dealerList)) {
            return (
                <span className="b-dealerships-view__empty-state-message">
                    {t('dealerships_view_no_devices_message')}
                </span>
            );
        }
    };

    const onClearValue = () => {
        fetchDealerships(1);
    };
    const onDealerTitleChange = value => {
        fetchDealerships(
            1,
            value.target.value,
            value.target.value,
        );
    };

    const handleGoToPortal = () => {
        window.open(Server.avpPortalNeRDPair, '_blank');
        setIsPortalPairModalOpen(false)
    }

    const debouncedDealerTitleChange = debounce(onDealerTitleChange, 1000);

    return (
        <div className="b-dealerships-view">
            {query 
                ?   <div className='back-button-wrapper'>
                        <GoBackButton />
                        <div className="result-message">
                            <p>
                                {t('dealership_search_results')}{' '}
                                <span>
                                    &ldquo; {query} &bdquo;
                                </span>
                            </p>
                        </div>
                    </div>
                :   <div className="filter-wrapper">
                        <div className="pair-text" onClick={handleOpenNewDevice}>
                            <IconButton
                                onClick={handleOpenNewDevice}
                                icon="system-add-large"
                                name="add-new-device"
                            />
                            <span>{t('pair_new_device')}</span>
                        </div>
                        <InputField
                            clearIcon
                            hideLabel={false}
                            name="dealerships-filter"
                            label={t('filter_dealerships')}
                            initialValue={dealerTitle}
                            onChangeHandler={debouncedDealerTitleChange}
                            onClearHandler={onClearValue}
                        />
                    </div>
            }

            {isLoading ? (
                <Loader />
            ) : (
                <>
                    {error ? (
                        <ErrorMessage style={{ marginTop: '20%' }}>
                            {error}
                        </ErrorMessage>
                    ) : (
                        renderContent()
                    )}
                    {renderPagination()}
                    {Config.isPinPairingEnabled && isPinPairModalOpen && (
                        <PinPairing
                            isOpen
                            onClose={handlePinPairModalClose}
                        />
                    )}
                    {isPortalPairModalOpen && (
                        <Modal
                            className="b-portal-pair"
                            localeAction={t('avp_portal')}
                            onAction={() => handleGoToPortal()}
                            onCancel={() => setIsPortalPairModalOpen(false)}
                            onClose={() => setIsPortalPairModalOpen(false)}
                            show={isPortalPairModalOpen}
                        >
                            <Modal.Title>{t('device_pair_notice')}:</Modal.Title>
                            <Modal.Content>
                                <p className="portal-message">
                                    {t('device_pair_portal_message')}
                                </p>
                            </Modal.Content>
                        </Modal>
                    )}
                </>
            )}
        </div>
    );
};

export default DealershipsView;